// Task 7: the rental lifecycle's vocabulary in the customer's and the staff
// member's own language. Every state is words, never a colour on its own and
// never a score or a percentage. Money is always formatted from integer
// centavos; nothing here ever does arithmetic on pesos.

export const RELEASE_COMPONENT_LABEL = {
  BOOKING: "Booking",
  CONTRACT: "Rental agreement",
  CUSTOMER_ACCEPTANCE: "Customer acceptance",
  PAYMENT: "Payment",
  ADVANCE: "Advance payment",
  SECURITY_DEPOSIT: "Security deposit",
  BUSINESS_LEGAL: "Business legal readiness",
  VEHICLE_LEGAL: "Vehicle legal readiness",
  VEHICLE_SAFETY: "Vehicle safety",
  DRIVER: "Driver",
  PICKUP_CONDITION: "Pickup condition record",
};

export const COMPONENT_STATUS_LABEL = {
  READY: "Ready",
  CHECK_REQUIRED: "Check required",
  REVIEW_REQUIRED: "Review required",
  BLOCKED: "Blocked",
  NOT_APPLICABLE: "Not applicable",
};

export const CLOSURE_STATE_LABEL = {
  OPEN: "Open",
  OPERATIONALLY_COMPLETE: "Vehicle back, money not settled",
  FINANCIALLY_CLOSED: "Ready to close",
  CLOSED: "Closed",
  REOPENED: "Reopened for reconciliation",
};

export const BLOCKER_LABEL = {
  CUSTOMER_CONFIRMATION_REQUIRED: "The customer has not confirmed this booking",
  MATERIAL_CHANGE_UNCONFIRMED: "The customer has not confirmed the latest changes",
  REQUIRED_PAYMENT_MISSING: "Required payment is not recorded",
  ADVANCE_MISSING: "The agreed advance is not recorded",
  SECURITY_DEPOSIT_MISSING: "The agreed security deposit is not recorded",
  BUSINESS_LEGAL_BLOCK: "A business legal requirement is not current",
  VEHICLE_LEGAL_BLOCK: "A vehicle legal requirement is not current",
  VEHICLE_SAFETY_BLOCK: "The vehicle has a safety hold",
  DRIVER_RELEASE_BLOCK: "The driver check is not passed",
  PICKUP_INSPECTION_INCOMPLETE: "The pickup condition record is not finished",
  BOOKING_NOT_CONFIRMED: "The booking is not confirmed",
  VEHICLE_NOT_RETURNED: "The vehicle is not recorded as returned",
  RETURN_INSPECTION_REQUIRED: "The return inspection is not finished",
  DAMAGE_ASSESSMENT_OPEN: "A damage case is still open",
  CHARGE_PROPOSAL_OPEN: "A charge proposal is waiting on a response",
  DEPOSIT_RECONCILIATION_OPEN: "The security deposit is not settled",
  REFUND_DUE: "A deposit refund is due",
  DISPUTE_OPEN: "A customer dispute is open",
  INSURANCE_CLAIM_OPEN: "An insurance claim is in progress",
  POST_RENTAL_CHARGE_PENDING: "A charge received after the rental is being handled",
  FINANCIAL_RECONCILIATION_REQUIRED: "The account does not balance yet",
  LEGACY_BOOKING_REVIEW: "This older booking has no recorded agreement",
};

export const DAMAGE_STATUS_LABEL = {
  REPORTED: "Reported",
  EVIDENCE_COLLECTION: "Collecting evidence",
  ASSESSMENT_PENDING: "Assessment pending",
  ASSESSMENT_COMPLETE: "Assessed",
  CUSTOMER_RESPONSE_PENDING: "Waiting on the customer",
  DISPUTED: "Disputed",
  INSURANCE_IN_PROGRESS: "With the insurer",
  REPAIR_IN_PROGRESS: "Being repaired",
  FINANCIAL_RECONCILIATION: "Settling the account",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
};

export const RESPONSIBILITY_LABEL = {
  NOT_DETERMINED: "Not determined",
  BUSINESS_ASSESSMENT_RENTER: "Rental business assessment: renter",
  BUSINESS: "Rental business",
  THIRD_PARTY: "Third party",
  INSURER: "Insurer",
  SHARED: "Shared",
  DISPUTED: "Disputed",
  LEGAL_REVIEW: "External legal review",
};

export const CHARGE_STATUS_LABEL = {
  DRAFT: "Draft",
  PROPOSED: "Proposed",
  CUSTOMER_NOTIFIED: "Proposed — waiting on the customer",
  AGREED: "Customer agreed",
  PARTIALLY_AGREED: "Customer partly agreed",
  INFORMATION_REQUESTED: "Customer asked for more information",
  DISPUTED: "Disputed",
  ESTABLISHED: "Established",
  WITHDRAWN: "Withdrawn",
  REVERSED: "Reversed",
};

export const DEPOSIT_STATE_LABEL = {
  NOT_REQUIRED: "No deposit required",
  NOT_RECEIVED: "Not received",
  HELD: "Held",
  PENDING_CHARGE: "Held — a charge is still open",
  PENDING_CLAIM: "Held — an insurance claim is in progress",
  PENDING_REVIEW: "Held — under review",
  DISPUTED: "Held — disputed",
  REFUND_DUE: "Refund due",
  PARTIALLY_APPLIED: "Partly applied",
  FULLY_APPLIED: "Fully applied",
  REFUNDED: "Refunded",
  SETTLED: "Settled",
};

export const DEPOSIT_APPLICATION_LABEL = {
  PROPOSED: "Proposed",
  CUSTOMER_NOTIFIED: "Proposed — waiting on the customer",
  AGREED: "Customer agreed",
  DISPUTED: "Disputed",
  APPROVED_BY_BUSINESS: "Approved by the business",
  RECORDED_APPLIED: "Applied",
  REVERSED: "Reversed",
  WITHDRAWN: "Withdrawn",
};

export const CLAIM_STATUS_LABEL = {
  NOT_APPLICABLE: "Not applicable",
  NOT_STARTED: "Not started",
  NOTICE_SENT: "Notice sent",
  DOCUMENTS_PENDING: "Documents pending",
  SUBMITTED: "Submitted",
  MORE_INFORMATION_REQUIRED: "More information required",
  UNDER_REVIEW: "Under review",
  APPROVED: "Approved",
  PARTIALLY_APPROVED: "Partly approved",
  DENIED: "Denied",
  PAID: "Paid",
  CLOSED: "Closed",
};

export const DISPUTE_STATUS_LABEL = {
  OPEN: "Open",
  BUSINESS_RESPONSE_REQUIRED: "Waiting on the business",
  CUSTOMER_RESPONSE_REQUIRED: "Waiting on the customer",
  EVIDENCE_REVIEW: "Reviewing evidence",
  PROPOSED_RESOLUTION: "Resolution proposed",
  RESOLVED: "Resolved",
  ESCALATED: "Escalated to ConfirmPH",
  CLOSED: "Closed",
};

export const PAYMENT_GATE_LABEL = {
  PAY_AT_PICKUP: "Payment is taken at pickup",
  ADVANCE_REQUIRED: "The agreed advance must be recorded before release",
  FULL_PAYMENT_BEFORE_RELEASE: "The rental must be recorded as paid in full before release",
  DEPOSIT_REQUIRED: "The security deposit must be recorded before release",
  CUSTOM_REVIEW: "A member of staff decides before each release",
};

// Tone is decoration only: the same information is always in the text beside it.
export function tone(status) {
  if (["READY", "RESOLVED", "CLOSED", "ESTABLISHED", "AGREED", "REFUNDED", "SETTLED"].includes(status)) {
    return "bg-emerald-50 text-emerald-800 border-emerald-200";
  }
  if (["BLOCKED", "DISPUTED", "DENIED"].includes(status)) {
    return "bg-rose-50 text-rose-800 border-rose-200";
  }
  if (["NOT_APPLICABLE", "WITHDRAWN", "REVERSED", "DRAFT"].includes(status)) {
    return "bg-slate-50 text-slate-600 border-slate-200";
  }
  return "bg-amber-50 text-amber-900 border-amber-200";
}

export function pesos(centavos) {
  if (centavos === null || centavos === undefined) return null;
  const negative = centavos < 0;
  const value = Math.abs(centavos);
  const whole = Math.trunc(value / 100);
  const text = `₱${whole.toLocaleString("en-PH")}.${String(value % 100).padStart(2, "0")}`;
  return negative ? `−${text}` : text;
}

export function blockerText(blocker) {
  return BLOCKER_LABEL[blocker.code] || blocker.message;
}

export function errorMessage(err, fallback = "Something went wrong.") {
  const d = err?.response?.data?.detail;
  if (typeof d === "string") return d;
  return d?.message || fallback;
}

export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result).split(",")[1]);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}
